import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/components/data-display/badge'
import { DataTable, type DataTableColumn } from '@/components/data-display/data-table'
import { useFeedback } from '@/components/feedback/feedback-context'
import { SearchInput } from '@/components/forms/search-input'
import { Select } from '@/components/forms/select'
import { FilterBar } from '@/components/layout/filter-bar'
import { PageHeader } from '@/components/layout/page-header'
import { ActionMenu } from '@/components/overlays/action-menu'
import { ConfirmationDialog } from '@/components/overlays/confirmation-dialog'
import { Button } from '@/components/primitives/button'
import actionDeleteIconUrl from '@/assets/action-menu/delete.svg'
import actionEditIconUrl from '@/assets/action-menu/edit.svg'
import { AdminShellPage } from '@/features/admin/shell/shell-page'

import {
  createOidcClient,
  deleteOidcClient,
  getOidcProviderConfiguration,
  listOAuthResources,
  listOidcClientScopes,
  listOidcClients,
  type OidcClient,
  type OidcClientCreated,
  type OidcClientKind,
} from './api'
import { OidcClientCredentialsModal } from './idp-client-credentials-modal'
import { OidcClientDetailDrawer } from './idp-client-detail-page'
import { OidcClientFormDrawer } from './idp-client-form-drawer'
import { IdpConfigurationFlow } from './idp-configuration-flow'
import { isOidcIdpReadOnly } from './idp-permissions'

const queryKey = ['admin-idp-clients']

type ClientKindFilter = '' | OidcClientKind

function clientKindFilterFromUrl(): ClientKindFilter {
  const value = new URL(window.location.href).searchParams.get('client_kind')
  return value === 'mcp_oauth' || value === 'oidc_embed' ? value : ''
}

function replaceClientKindFilterInUrl(value: ClientKindFilter) {
  const url = new URL(window.location.href)
  if (value) {
    url.searchParams.set('client_kind', value)
  } else {
    url.searchParams.delete('client_kind')
  }
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
}

export function AdminOidcClientsPage() {
  const { t } = useTranslation('adminIdp')
  const queryClient = useQueryClient()
  const { showFeedback } = useFeedback()
  const readOnly = isOidcIdpReadOnly()
  const [search, setSearch] = useState('')
  const [clientKind, setClientKind] = useState<ClientKindFilter>(clientKindFilterFromUrl)
  const [createOpen, setCreateOpen] = useState(false)
  const [createdClient, setCreatedClient] = useState<OidcClientCreated | null>(null)
  const [editingClientRecordId, setEditingClientRecordId] = useState<string | null>(null)
  const [pendingDeleteClient, setPendingDeleteClient] = useState<OidcClient | null>(null)
  const clientsQuery = useQuery({ queryFn: listOidcClients, queryKey })
  const resourcesQuery = useQuery({
    queryFn: listOAuthResources,
    queryKey: ['admin-idp-oauth-resources'],
  })
  const scopesQuery = useQuery({
    queryFn: listOidcClientScopes,
    queryKey: ['admin-idp-client-scopes'],
  })
  const providerQuery = useQuery({
    queryFn: getOidcProviderConfiguration,
    queryKey: ['admin-idp-provider'],
  })
  const createMutation = useMutation({
    mutationFn: createOidcClient,
    onError: () => showFeedback({ message: t('clients.errors.create'), tone: 'error' }),
    onSuccess: (client) => {
      void queryClient.invalidateQueries({ queryKey })
      setCreateOpen(false)
      setCreatedClient(client)
      showFeedback({ message: t('clients.success.created'), tone: 'success' })
    },
  })
  const deleteMutation = useMutation({
    mutationFn: deleteOidcClient,
    onError: () => showFeedback({ message: t('clients.errors.delete'), tone: 'error' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey })
      setPendingDeleteClient(null)
      showFeedback({ message: t('clients.success.deleted'), tone: 'success' })
    },
  })

  const rows = useMemo(() => {
    const normalized = search.trim().toLocaleLowerCase()
    return (clientsQuery.data ?? []).filter((client) => {
      if (clientKind && client.client_kind !== clientKind) return false
      if (!normalized) return true
      return [
        client.name,
        client.client_id,
        client.resource_uri ?? '',
        ...client.applications.map((app) => app.name),
      ]
        .join(' ')
        .toLocaleLowerCase()
        .includes(normalized)
    })
  }, [clientKind, clientsQuery.data, search])

  const clientKindOptions = useMemo(
    () => [
      { label: t('clients.filters.allClientKinds'), value: '' },
      { label: t('clients.clientKinds.oidcEmbed'), value: 'oidc_embed' },
      { label: t('clients.clientKinds.mcpOauth'), value: 'mcp_oauth' },
    ],
    [t],
  )

  const columns = useMemo<DataTableColumn<OidcClient>[]>(
    () => [
      {
        header: t('clients.columns.clientKind'),
        id: 'clientKind',
        render: (client) => (
          <Badge
            className="gap-1.5"
            variant={client.client_kind === 'mcp_oauth' ? 'info' : 'neutral'}
          >
            <span aria-hidden className="size-1.5 rounded-full bg-current opacity-70" />
            {client.client_kind === 'mcp_oauth'
              ? t('clients.clientKinds.mcpOauth')
              : t('clients.clientKinds.oidcEmbed')}
          </Badge>
        ),
        width: 150,
      },
      {
        header: t('clients.columns.name'),
        id: 'name',
        render: (client) => <span className="font-medium">{client.name}</span>,
        width: 220,
      },
      {
        header: t('clients.columns.clientId'),
        id: 'clientId',
        render: (client) => (
          <code className="text-xs text-[var(--color-text-muted)]">{client.client_id}</code>
        ),
        width: 280,
      },
      {
        header: t('clients.columns.scopes'),
        id: 'scopes',
        render: (client) => (
          <span className="text-sm text-[var(--color-text-muted)]">
            {client.client_scope_assignments.map((item) => item.scope_name).join(', ') ||
              (client.client_kind === 'oidc_embed' ? 'openid' : '—')}
          </span>
        ),
        width: 220,
      },
      {
        align: 'center',
        header: t('clients.columns.applications'),
        id: 'applications',
        render: (client) =>
          client.client_kind === 'mcp_oauth' ? '—' : client.application_ids.length,
        width: 110,
      },
      {
        header: t('clients.columns.status'),
        id: 'status',
        render: (client) => (
          <Badge variant={client.status === 'active' ? 'success' : 'neutral'}>
            {client.status === 'active' ? t('status.enabled') : t('status.disabled')}
          </Badge>
        ),
        width: 100,
      },
      {
        align: 'right',
        header: t('clients.columns.actions'),
        id: 'actions',
        pin: 'right',
        render: (client) => (
          <ActionMenu
            ariaLabel={t('clients.rowActions.open', { name: client.name })}
            items={[
              {
                iconSrc: actionEditIconUrl,
                key: 'edit',
                label: t('clients.rowActions.edit'),
                onSelect: () => setEditingClientRecordId(client.id),
              },
              ...(readOnly
                ? []
                : [
                    {
                      disabled: deleteMutation.isPending,
                      iconSrc: actionDeleteIconUrl,
                      key: 'delete',
                      label: t('actions.delete'),
                      onSelect: () => setPendingDeleteClient(client),
                      variant: 'danger' as const,
                    },
                  ]),
            ]}
          />
        ),
        width: 90,
      },
    ],
    [deleteMutation, readOnly, t],
  )

  return (
    <AdminShellPage activePath="/admin/idp/clients">
      <div className="flex h-full flex-col overflow-hidden bg-white">
        <PageHeader
          actions={
            readOnly ? null : (
              <Button onClick={() => setCreateOpen(true)}>{t('clients.create')}</Button>
            )
          }
          description={t('clients.description')}
          title={t('clients.title')}
        />
        <div className="px-5 pt-5 lg:px-[30px]">
          <div className="mb-5">
            <IdpConfigurationFlow active="clients" />
          </div>
        </div>
        <FilterBar>
          <SearchInput
            aria-label={t('clients.search')}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('clients.search')}
            value={search}
          />
          <Select
            aria-label={t('clients.filters.clientKind')}
            onValueChange={(value) => {
              const nextValue = value as ClientKindFilter
              setClientKind(nextValue)
              replaceClientKindFilterInUrl(nextValue)
            }}
            options={clientKindOptions}
            value={clientKind}
          />
        </FilterBar>
        <div className="min-h-0 flex-1 overflow-auto px-5 py-4 lg:px-[30px]">
          {clientsQuery.isError ? (
            <p className="text-sm text-[var(--color-danger)]">{t('clients.errors.load')}</p>
          ) : (
            <DataTable
              columns={columns}
              empty={
                <div className="py-10 text-center text-sm text-[var(--color-text-muted)]">
                  {t('clients.empty')}
                </div>
              }
              getRowKey={(client) => client.id}
              isLoading={clientsQuery.isLoading}
              minWidth={1020}
              rows={rows}
            />
          )}
        </div>
      </div>

      {readOnly ? null : (
        <OidcClientFormDrawer
          clientScopes={scopesQuery.data ?? []}
          isSaving={createMutation.isPending}
          key={createOpen ? 'open' : 'closed'}
          onOpenChange={setCreateOpen}
          onSave={(payload) => createMutation.mutate(payload)}
          open={createOpen}
          resources={resourcesQuery.data ?? []}
        />
      )}
      <OidcClientCredentialsModal
        client={createdClient}
        onClose={() => {
          const clientId = createdClient?.id
          setCreatedClient(null)
          if (clientId) {
            setEditingClientRecordId(clientId)
          }
        }}
        onRetryProvider={() => void providerQuery.refetch()}
        provider={providerQuery.data ?? null}
        providerError={providerQuery.isError}
        providerLoading={providerQuery.isFetching}
      />
      {editingClientRecordId ? (
        <OidcClientDetailDrawer
          clientRecordId={editingClientRecordId}
          key={editingClientRecordId}
          onOpenChange={(open) => {
            if (!open) setEditingClientRecordId(null)
          }}
          open
        />
      ) : null}
      <ConfirmationDialog
        cancelLabel={t('actions.cancel')}
        confirmLabel={t('actions.delete')}
        description={t('clients.confirmDelete', { name: pendingDeleteClient?.name ?? '' })}
        isConfirming={deleteMutation.isPending}
        onCancel={() => setPendingDeleteClient(null)}
        onConfirm={() => {
          if (pendingDeleteClient) deleteMutation.mutate(pendingDeleteClient.id)
        }}
        open={Boolean(pendingDeleteClient)}
        title={t('clients.confirmDeleteTitle')}
        tone="danger"
      />
    </AdminShellPage>
  )
}
